import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import StudioAura, { BrandMark } from '../components/StudioAura'

const features = [
  { title: 'Track-individuell', desc: 'Analyse steuert EQ, Dynamics und Loudness – nicht ein festes Preset für alles.', icon: '01' },
  { title: 'Plattform-Ziele', desc: 'LUFS, True Peak und QC für Streaming, Radio und Club.', icon: '02' },
  { title: 'Cover & Canvas', desc: 'Artwork und Spotify-Canvas optional im gleichen Workflow.', icon: '03' },
]

function formatPrice(cents, currency = 'eur') {
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: (currency || 'eur').toUpperCase() }).format((cents || 0) / 100)
  } catch {
    return `${((cents || 0) / 100).toFixed(2)} ${(currency || 'EUR').toUpperCase()}`
  }
}

export default function Landing() {
  const [pricing, setPricing] = useState({ enabled: false, products: [], job_cost_credits: 1 })

  useEffect(() => {
    api.get('/billing/public-products')
      .then((r) => setPricing(r.data || { enabled: false, products: [], job_cost_credits: 1 }))
      .catch(() => setPricing({ enabled: false, products: [], job_cost_credits: 1 }))
  }, [])

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-[#e8e8ed] safe-area-padding">
      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-[#0a0a0f]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2.5 text-[#00d4aa] hover:opacity-90">
            <BrandMark size={30} />
            <span className="text-[15px] font-semibold tracking-tight text-[#e8e8ed]">
              AI Mastering <span className="font-normal text-white/45">Studio</span>
            </span>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            <a href="#preise" className="hidden sm:inline rounded-lg px-3 py-2 text-sm text-white/50 hover:text-white/90">Preise</a>
            <Link to="/login" className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-white/90 hover:bg-white/5 transition">Anmelden</Link>
            <Link to="/register" className="rounded-lg bg-[#00d4aa] px-4 py-2 text-sm font-semibold text-[#0a0a0f] hover:bg-[#1ae0b8] transition">Registrieren</Link>
          </nav>
        </div>
      </header>

      <main className="relative mx-auto max-w-6xl px-4 pb-20 pt-12 sm:pt-16">
        {/* Hero: Brand zuerst, Aura als Hintergrund – keine Ribbon-/Dropzone-Kopie */}
        <section className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-[#12121a]">
          <StudioAura height="100%" className="absolute inset-0 opacity-90" />
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: 'linear-gradient(to top, #12121a 8%, transparent 55%)' }}
          />
          <div className="relative z-10 flex min-h-[380px] flex-col items-start justify-end gap-6 px-8 py-12 sm:min-h-[420px] sm:px-12 sm:py-14">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#00d4aa]">anwendungen.online</p>
            <h1 className="max-w-2xl text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
              AI Mastering Studio
            </h1>
            <p className="max-w-lg text-base text-white/55 sm:text-lg">
              Lade deinen Mix hoch, wähle ein Zielprofil – und hole dir einen release-fähigen Master inkl. QC.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                className="rounded-lg bg-[#00d4aa] px-6 py-3 text-sm font-semibold text-[#0a0a0f] transition hover:bg-[#1ae0b8]"
              >
                Konto erstellen
              </Link>
              <Link
                to="/login"
                className="rounded-lg border border-white/15 bg-black/30 px-6 py-3 text-sm font-medium text-white/85 backdrop-blur transition hover:bg-white/10"
              >
                Zum Upload anmelden
              </Link>
            </div>
          </div>
        </section>

        <section className="mt-14 grid gap-5 sm:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-white/[0.06] bg-[#12121a] p-6">
              <p className="font-mono text-xs text-[#00d4aa]">{f.icon}</p>
              <h3 className="mt-3 text-lg font-semibold text-white">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/50">{f.desc}</p>
            </div>
          ))}
        </section>

        <section id="preise" className="mt-16 scroll-mt-24">
          <div className="rounded-2xl border border-white/[0.06] bg-[#12121a] p-8 text-center">
            <h2 className="text-xl font-semibold text-white">Preise</h2>
            <p className="mt-2 text-white/50">
              {pricing.job_cost_credits || 1} Credit = 1 Mastering-Job. Zahlung über Stripe.
            </p>
            {!pricing.enabled && (
              <p className="mt-4 text-sm text-amber-200/80">Verkauf wird freigeschaltet – Registrierung ist möglich.</p>
            )}
            {pricing.enabled && pricing.products?.length > 0 && (
              <div className="mt-8 grid gap-4 text-left sm:grid-cols-2 lg:grid-cols-3">
                {pricing.products.map((p) => (
                  <div key={p.id} className="rounded-xl border border-white/10 bg-[#0a0a0f] p-5">
                    <div className="font-medium text-white">{p.name}</div>
                    <div className="mt-1 text-sm text-white/45">{p.credits} Credits</div>
                    <div className="mt-4 text-2xl font-semibold text-white">{formatPrice(p.price_cents, p.currency)}</div>
                    <Link
                      to="/register"
                      className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-[#00d4aa] px-4 py-2.5 text-sm font-semibold text-[#0a0a0f] hover:bg-[#1ae0b8]"
                    >
                      Kaufen nach Login
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/[0.06] py-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 text-sm text-white/40">
          <span>© AI Mastering Studio · anwendungen.online</span>
          <div className="flex flex-wrap gap-4">
            <Link to="/impressum" className="hover:text-white/70">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white/70">Datenschutz</Link>
            <Link to="/agb" className="hover:text-white/70">AGB</Link>
            <Link to="/nutzungsbedingungen" className="hover:text-white/70">Nutzungsbedingungen</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
